/**
 * This module defines the Rename action and its bindings to an `ItemsScope`.
 */
import * as React from 'react';
import { renameItem as defaultRenameItem } from '../../../../operations/renameItem';
import {
  IDecorateWithSelectedItemsProps,
  IDecorateWithExactlyOneItemProps,
  withSelectedItems,
  withExactlyOneItem
} from '../../../decorators/selectors';
import { renameActionControl, IRenameWizardProps } from '../../../../controls/renameActionControl';
import { WizardAction, IWizardActionStateProps } from '../../../wizardAction/WizardAction';
import { fileNameFacet, malwareDetectedFacet } from '../../../../facets/ItemFacets';
import { getItemActionEngagement } from '../../../../models/telemetry/getItemEngagement';
import { connectToItemCache } from '../../../decorators/connectToItemCache';
import { IItemCache } from '../../../../models/IItemCacheObserver';
import { ISelectedActionProps, ActionSelector } from '../../../withAction/WithAction';

export interface IItemRenameActionProps {
  itemKey: string;
  renameWizard: React.ComponentType<IRenameWizardProps>;
  renameItem?: typeof defaultRenameItem;
}

export const ItemRenameAction: React.ComponentType<IItemRenameActionProps> = connectToItemCache((
  itemCache: IItemCache,
  props: IItemRenameActionProps
): ISelectedActionProps => {
  const {
    itemKey,
    renameWizard,
    renameItem = defaultRenameItem
  } = props;

  if (!itemKey) {
    return {
      action: null
    };
  }

  const isMalwareDetected = itemCache.demandItemFacet(malwareDetectedFacet, itemKey);
  let isAvailable = !isMalwareDetected;

  let FinalRenameWizard = renameWizard;

  const fileName = itemCache.demandItemFacet(fileNameFacet, itemKey);

  const oldName = fileName && fileName.name || '';

  const controlHandler = itemCache.demandItemFacet(renameActionControl, itemKey);

  const {
    isAvailable: itemIsAvailable,
    renameWizard: ItemRenameWizard,
    validator,
    lockedWarningMessage,
    title
  } = renameActionControl.evaluate(controlHandler)(itemCache, {
    itemKey: itemKey,
    isAvailable: false,
    renameWizard: renameWizard
  });

  if (!itemIsAvailable) {
    isAvailable = false;
  }

  FinalRenameWizard = ItemRenameWizard;

  const { dispatch } = itemCache;

  const onRename = async ({ name }: { name: string; }) => {
    await dispatch(renameItem({
      items: {
        [itemKey]: {
          names: {
            [oldName]: {
              newName: name
            }
          }
        }
      }
    }));
  };

  return {
    action: (
      <WizardAction
        isAvailable={ isAvailable }
        { ...getItemActionEngagement(itemCache, {
          itemKey,
          engagement: {
            name: 'Rename',
            isIntentional: true
          }
        }) }
      >
        { (wizardProps: IWizardActionStateProps) => {
          return (
            <FinalRenameWizard
              { ...wizardProps }
              isAvailable={ isAvailable && !!itemKey && !!fileName }
              itemKey={ itemKey }
              onRename={ onRename }
              oldName={ oldName }
              lockedWarningMessage={ lockedWarningMessage }
              fileExtensionText={ fileName && fileName.extension }
              title={ title }
              validator={ validator }
            />
          );
        } }
      </WizardAction>
    )
  };
})(ActionSelector);

export type IRenameActionProps = IDecorateWithSelectedItemsProps<IDecorateWithExactlyOneItemProps<IItemRenameActionProps>>;

export const RenameAction: React.ComponentType<IRenameActionProps> = withSelectedItems()(withExactlyOneItem()(ItemRenameAction));
